import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  Spin,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  SaveOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';

const DeliveryCostPage = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const [deliveryCosts, setDeliveryCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    country: 'armenia',
    auction: '',
    search: '',
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadDeliveryCosts();
    loadStatistics();
  }, [filters]);

  const loadDeliveryCosts = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 1000, ...filters };
      const response = await DashboardService.getDeliveryFees(params);

      if (response.success) {
        setDeliveryCosts(response.data || []);
        setTotalCount(response.pagination?.total || 0);
      } else {
        showError(response.message || t('deliveryCost.loadError'));
      }
    } catch (error) {
      console.error('Error loading delivery costs:', error);
      showError(t('deliveryCost.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await DashboardService.getDeliveryFeeStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ auction: 'copart', country: 'armenia' });
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    form.setFieldsValue({
      location: item.location,
      fee: item.fee,
      notes: item.notes || '',
      auction: item.auction,
      country: item.country,
    });
    setIsFormOpen(true);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      let response;
      if (editingItem) {
        response = await DashboardService.updateDeliveryFee(editingItem.id, values);
      } else {
        response = await DashboardService.createDeliveryFee(values);
      }

      if (response.success) {
        showSuccess(editingItem ? t('deliveryCost.updateSuccess') : t('deliveryCost.createSuccess'));
        setIsFormOpen(false);
        loadDeliveryCosts();
        loadStatistics();
      } else {
        showError(response.message || t('deliveryCost.saveError'));
      }
    } catch (error) {
      if (error.errorFields) return;
      showError(t('deliveryCost.saveError'));
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await DashboardService.deleteDeliveryFee(deleteId);

      if (response.success) {
        showSuccess(t('deliveryCost.deleteSuccess'));
        setDeleteModalOpen(false);
        setDeleteId(null);
        loadDeliveryCosts();
        loadStatistics();
      } else {
        showError(response.message || t('deliveryCost.deleteError'));
      }
    } catch (error) {
      console.error('Error deleting delivery cost:', error);
      showError(t('deliveryCost.deleteError'));
    }
  };

  const handleInlineEdit = (item) => {
    setEditingId(item.id);
    editForm.setFieldsValue({ fee: item.fee, notes: item.notes || '' });
  };

  const handleInlineSave = async (id) => {
    try {
      const values = await editForm.validateFields();
      const response = await DashboardService.updateDeliveryFee(id, values);

      if (response.success) {
        showSuccess(t('deliveryCost.updateSuccess'));
        setEditingId(null);
        loadDeliveryCosts();
      } else {
        showError(response.message || t('deliveryCost.saveError'));
      }
    } catch (error) {
      if (error.errorFields) return;
    }
  };

  const handleInlineCancel = () => {
    setEditingId(null);
  };

  const clearSearch = () => {
    setFilters((prev) => ({ ...prev, search: '' }));
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: t('deliveryCost.location'), dataIndex: 'location', key: 'location' },
    {
      title: t('deliveryCost.fee'),
      dataIndex: 'fee',
      key: 'fee',
      render: (val, record) =>
        editingId === record.id ? (
          <Form form={editForm} layout="vertical" onFinish={() => handleInlineSave(record.id)} style={{ maxWidth: 200 }}>
            <Form.Item name="fee" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="notes" style={{ marginBottom: 8 }}>
              <Input size="small" placeholder={t('deliveryCost.addNotes')} />
            </Form.Item>
            <Flex gap={4}>
              <Button type="link" size="small" htmlType="submit" icon={<SaveOutlined />} />
              <Button type="link" size="small" onClick={handleInlineCancel} icon={<CloseOutlined />} />
            </Flex>
          </Form>
        ) : (
          <Tag color={val === 'call' ? 'orange' : 'green'}>${val}</Tag>
        ),
    },
    {
      title: t('deliveryCost.notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (val, record) => (editingId === record.id ? '-' : val || '-'),
    },
    {
      title: t('deliveryCost.auction'),
      dataIndex: 'auction',
      key: 'auction',
      render: (v) => <Tag color={v === 'copart' ? 'blue' : v === 'iaai' ? 'purple' : 'cyan'}>{v?.toUpperCase()}</Tag>,
    },
    {
      title: t('deliveryCost.country'),
      dataIndex: 'country',
      key: 'country',
      render: (v) => (
        <Tag>{v ? v.charAt(0).toUpperCase() + v.slice(1) : '-'}</Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) =>
        editingId === record.id ? null : (
          <Flex gap={4} justify="center">
            <Tooltip title={t('common.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleInlineEdit(record)}
              />
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(record.id)}
              />
            </Tooltip>
          </Flex>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('deliveryCost.title')}
        </Typography.Title>
        <Flex gap={8}>
          <Button icon={<ReloadOutlined />} onClick={loadDeliveryCosts}>
            {t('common.refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            {t('deliveryCost.addNew')}
          </Button>
        </Flex>
      </Flex>

      {statistics && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <Card>
              <Flex justify="space-between" align="center">
                <Flex vertical>
                  <Typography.Text type="secondary">{t('deliveryCost.totalLocations')}</Typography.Text>
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    {statistics.total || 0}
                  </Typography.Title>
                </Flex>
                <EnvironmentOutlined style={{ fontSize: 40, color: 'var(--colorPrimary)' }} />
              </Flex>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Flex justify="space-between" align="center">
                <Flex vertical>
                  <Typography.Text type="secondary">{t('deliveryCost.armenia')}</Typography.Text>
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    {statistics.armenia?.total || 0}
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('deliveryCost.statsCopart')} {statistics.armenia?.copart || 0}, {t('deliveryCost.statsIaai')} {statistics.armenia?.iaai || 0}, {t('deliveryCost.statsManheim')} {statistics.armenia?.manheim || 0}
                  </Typography.Text>
                </Flex>
                <GlobalOutlined style={{ fontSize: 40, color: '#22c55e' }} />
              </Flex>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Flex justify="space-between" align="center">
                <Flex vertical>
                  <Typography.Text type="secondary">{t('deliveryCost.georgia')}</Typography.Text>
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    {statistics.georgia?.total || 0}
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('deliveryCost.statsCopart')} {statistics.georgia?.copart || 0}, {t('deliveryCost.statsIaai')} {statistics.georgia?.iaai || 0}, {t('deliveryCost.statsManheim')} {statistics.georgia?.manheim || 0}
                  </Typography.Text>
                </Flex>
                <CarOutlined style={{ fontSize: 40, color: '#3b82f6' }} />
              </Flex>
            </Card>
          </Col>
        </Row>
      )}

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              {t('deliveryCost.country')}
            </Typography.Text>
            <Select
              value={filters.country}
              onChange={(v) => setFilters((prev) => ({ ...prev, country: v }))}
              options={[
                { label: t('deliveryCost.armenia'), value: 'armenia' },
                { label: t('deliveryCost.georgia'), value: 'georgia' },
              ]}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              {t('deliveryCost.auction')}
            </Typography.Text>
            <Select
              value={filters.auction}
              onChange={(v) => setFilters((prev) => ({ ...prev, auction: v }))}
              options={[
                { label: t('common.all'), value: '' },
                { label: t('deliveryCost.auctionCopart'), value: 'copart' },
                { label: t('deliveryCost.auctionIaai'), value: 'iaai' },
                { label: t('deliveryCost.auctionManheim'), value: 'manheim' },
              ]}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              {t('deliveryCost.searchLocation')}
            </Typography.Text>
            <Input
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder={t('deliveryCost.searchPlaceholder')}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      {totalCount > 0 && (
        <Alert
          type="info"
          message={`Showing ${deliveryCosts.length} of ${totalCount} locations${filters.country ? ` in ${filters.country.charAt(0).toUpperCase() + filters.country.slice(1)}` : ''}${filters.auction ? ` (${filters.auction.toUpperCase()})` : ''}`}
          style={{ marginBottom: 24 }}
        />
      )}

      {loading ? (
        <Flex justify="center" align="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={deliveryCosts.map((c) => ({ ...c, key: c.id }))}
            locale={{ emptyText: t('common.noDataFound') }}
          />
        </Card>
      )}

      <Modal
        title={editingItem ? t('deliveryCost.editLocation') : t('deliveryCost.addNew')}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        onOk={handleFormSubmit}
        okText={editingItem ? t('common.update') : t('common.create')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="location" label={t('deliveryCost.location')} rules={[{ required: true }]}>
            <Input placeholder="e.g., ATLANTA -Georgia-Savannah" />
          </Form.Item>
          <Form.Item name="fee" label={t('deliveryCost.fee')} rules={[{ required: true }]}>
            <Input placeholder="e.g., 2395 or call" />
          </Form.Item>
          <Form.Item name="auction" label={t('deliveryCost.auction')} rules={[{ required: true }]}>
            <Select
              options={[
                { label: t('deliveryCost.auctionCopart'), value: 'copart' },
                { label: t('deliveryCost.auctionIaai'), value: 'iaai' },
                { label: t('deliveryCost.auctionManheim'), value: 'manheim' },
              ]}
            />
          </Form.Item>
          <Form.Item name="country" label={t('deliveryCost.country')} rules={[{ required: true }]}>
            <Select
              options={[
                { label: t('deliveryCost.armenia'), value: 'armenia' },
                { label: t('deliveryCost.georgia'), value: 'georgia' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('deliveryCost.notes')}>
            <Input.TextArea rows={3} placeholder={t('deliveryCost.notesPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('deliveryCost.confirmDelete')}
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={handleDeleteConfirm}
        okText={t('common.delete')}
        okButtonProps={{ danger: true }}
      >
        <Typography.Text>{t('deliveryCost.confirmDeleteMessage')}</Typography.Text>
      </Modal>
    </div>
  );
};

export default DeliveryCostPage;
